import type { ListingSeo, ListingFaqItem } from '../../domain/models/Seo';

/** Map a raw API plain-object (or `PlpSeoDataResponseDTO`) to `ListingSeo`.
 *
 * Handles three shapes:
 *  - top-level flat object (direct API response)
 *  - nested under a `listingSeo` key (new specific naming)
 *  - nested under a `seo` key (legacy - kept for backward compatibility)
 */
export function plainToListingSeo(plain: Record<string, unknown>): ListingSeo | undefined {
  const seoNested = (plain.listingSeo && typeof plain.listingSeo === 'object' && !Array.isArray(plain.listingSeo))
    ? (plain.listingSeo as Record<string, unknown>) 
    : (plain.seo && typeof plain.seo === 'object' && !Array.isArray(plain.seo))
    ? (plain.seo as Record<string, unknown>) : undefined;
  const canonical = typeof plain.canonical === 'string' ? plain.canonical
    : typeof seoNested?.canonical === 'string' ? seoNested.canonical : undefined;
  const description = typeof plain.description === 'string' ? plain.description
    : typeof seoNested?.description === 'string' ? seoNested.description : undefined;
  const header = typeof plain.header === 'string' ? plain.header
    : typeof seoNested?.header === 'string' ? seoNested.header : undefined;
  const keywords = typeof plain.keywords === 'string' ? plain.keywords
    : typeof seoNested?.keywords === 'string' ? seoNested.keywords : undefined;
  const robotsMeta = typeof plain.robotsMeta === 'string' ? plain.robotsMeta
    : typeof seoNested?.robotsMeta === 'string' ? seoNested.robotsMeta : undefined;
  const schema = plain.schema ?? seoNested?.schema;
  const seoText = typeof plain.seoText === 'string' ? plain.seoText
    : typeof seoNested?.seoText === 'string' ? seoNested.seoText : undefined;
  const shareUrl = typeof plain.shareUrl === 'string' ? plain.shareUrl
    : typeof seoNested?.shareUrl === 'string' ? seoNested.shareUrl : undefined;
  const title = typeof plain.title === 'string' ? plain.title
    : typeof seoNested?.title === 'string' ? seoNested.title : undefined;
  const rawSeoLinks = plain.seoLinks ?? (seoNested?.seoLinks);
  const seoLinks = Array.isArray(rawSeoLinks)
    ? (rawSeoLinks as Array<{ label?: string; displayName?: string; url?: string; hoverTitle?: string | null }>).map((item) => ({
        label: String(item?.label ?? item?.displayName ?? ''),
        url: String(item?.url ?? '').replace('__BASE_URL__', 'https://www.bluestone.com'),
        hoverTitle: item?.hoverTitle ?? null,
      })).filter((l) => l.url)
    : undefined;
  const prev = typeof plain.prev === 'string' ? plain.prev
    : typeof seoNested?.prev === 'string' ? seoNested.prev : undefined;
  const next = typeof plain.next === 'string' ? plain.next
    : typeof seoNested?.next === 'string' ? seoNested.next : undefined;
  if (!canonical && !description && !header && !keywords && !robotsMeta && !schema && !seoText && !shareUrl && !title && !seoLinks?.length && !prev && !next) {
    return undefined;
  }
  return {
    ...(canonical && { canonical }),
    ...(description && { description }),
    ...(header && { header }),
    ...(keywords && { keywords }),
    ...(robotsMeta && { robotsMeta }),
    ...(schema != null && { schema }),
    ...(seoLinks?.length && { seoLinks }),
    ...(seoText && { seoText }),
    ...(shareUrl && { shareUrl }),
    ...(title && { title }),
    ...(prev && { prev }),
    ...(next && { next }),
  };
}

export function adaptFaqsFromPlain(plain: Record<string, unknown>): ListingFaqItem[] | undefined {
  const raw = plain.faqs;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const items = raw
    .map((item): ListingFaqItem | null => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const question = typeof record.question === 'string' ? record.question.trim() : '';
      const answer = typeof record.answer === 'string' ? record.answer.trim() : '';
      if (!question || !answer) return null;
      return { question, answer };
    })
    .filter((item): item is ListingFaqItem => item != null);
  return items.length > 0 ? items : undefined;
}
